import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  Typography,
  Chip,
  Tooltip,
  IconButton,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Business as BusinessIcon,
  CurrencyExchange as CurrencyIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { setBranch } from '../store/slices/authSlice';
import { RootState } from '../store';
import { userBranchPreferencesService, Branch } from '../services/userBranchPreferences.service';

interface BranchSelectorProps {
  onBranchChange?: (branch: Branch) => void;
  showCurrency?: boolean;
}

const BranchSelector: React.FC<BranchSelectorProps> = ({ 
  onBranchChange, 
  showCurrency = true 
}) => {
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const user = useSelector((state: any) => state.auth.user);
  const dispatch = useDispatch();
  const globalBranch = useSelector((state: RootState) => state.auth.branch);

  useEffect(() => {
    fetchBranches();
  }, []);

  const fetchBranches = async () => {
    try {
      setLoading(true);
      const accessibleBranches = await userBranchPreferencesService.getUserAccessibleBranches();
      setBranches(accessibleBranches);

      // Get user's default branch
      const defaultBranch = await userBranchPreferencesService.getUserDefaultBranch();
      dispatch(setBranch(defaultBranch.branch));
    } catch (error) {
      setError('Failed to load branches');

    } finally {
      setLoading(false);
    }
  };

  const handleBranchChange = async (branchId: string) => {
    try {
      const branch = branches.find(b => b.branch_id === branchId);
      if (!branch) return;
      // Set as default branch
      await userBranchPreferencesService.setUserDefaultBranch(branchId);
      // Update global branch in Redux
      dispatch(setBranch(branch));
      // Notify parent component
      if (onBranchChange) {
        onBranchChange(branch);
      }
    } catch (error) {

    }
  };

  // Only show for Admin and SuperAdmin
  if (user?.role !== 'Admin' && user?.role !== 'SuperAdmin') {
    return null;
  }

  if (loading) {
    return (
      <Box display="flex" alignItems="center" gap={1}>
        <CircularProgress size={20} />
        <Typography variant="body2">Loading branches...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box display="flex" alignItems="center" gap={2}>
      <Box display="flex" alignItems="center" gap={1}>
        <BusinessIcon color="primary" />
        <Typography variant="body2" color="text.secondary">
          Branch:
        </Typography>
      </Box>

      <FormControl size="small" sx={{ minWidth: 200 }}>
        <Select
          value={globalBranch?.branch_id || ''}
          onChange={(e) => handleBranchChange(e.target.value)}
          displayEmpty
          sx={{
            '& .MuiSelect-select': {
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            },
          }}
        >
          {branches.map((branch) => (
            <MenuItem key={branch.branch_id} value={branch.branch_id}>
              <Box display="flex" alignItems="center" gap={1} width="100%">
                <Box flex={1}>
                  <Typography variant="body2" fontWeight="medium">
                    {branch.branch_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {branch.location}
                  </Typography>
                </Box>
                {showCurrency && (
                  <Chip
                    label={`${branch.currency_symbol} ${branch.currency_code}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {globalBranch && showCurrency && (
        <Tooltip title={`Tax Rate: ${globalBranch.tax_rate}%`}>
          <Chip
            icon={<CurrencyIcon />}
            label={`${globalBranch.currency_symbol} ${globalBranch.currency_code}`}
            color="primary"
            size="small"
          />
        </Tooltip>
      )}
    </Box>
  );
};

export default BranchSelector; 